// noinspection SpellCheckingInspection

import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import { useEffect, useState } from "react";
import Proposal from "work/entities/proposal/proposal";
import { styled } from "@mui/material/styles";
import { useSession } from "users/session/session-context";
import { Masked } from "../../work-agreement/work-agreement";

const AudienceLabel = styled("span")<{ isexternal: boolean, disabled?: boolean }>(
  ({ theme, isexternal, disabled }) => ({
    color: isexternal ? theme.palette.error.main : theme.palette.common.black,
    fontWeight: isexternal ? theme.typography.fontWeightBold : undefined,
    opacity: disabled ? 0.5 : 1,
  })
);

type AudienceSwitchProps = {
  className?: string;
  proposal: Proposal;
  disabled?: boolean;
  isExternal?: boolean;
  onIsExternalChanged: (isExternal: boolean) => void;
};

export default function AudienceSwitch(props: Readonly<AudienceSwitchProps>) {
  const {
    disabled,
    proposal,
    onIsExternalChanged
  } = props;

  const [isExternal, setIsExternal] = useState(props.isExternal ?? false);
  const session = useSession();

  const isClient = proposal?.client?.userId.isEqualTo(session.user?.id);
  const isTeamLeader = Masked.isPresentNotMasked(proposal.vendorTeam) &&
    !!proposal.vendorTeam.leader?.userId.isEqualTo(session.user?.id);

  useEffect(() => {
    setIsExternal(props.isExternal ?? false);
  }, [props.isExternal]);

  function handleIsExternalChanged() {
    onIsExternalChanged(!isExternal);
    setIsExternal(prev => !prev);
  }

  if (proposal && !isClient && !isTeamLeader) return null;

  return (
    <Tooltip title={disabled ? "You cannot change a comment from external to internal" : ""}>
      <span>
        <FormControl component="span" variant="standard">
          <FormControlLabel
            control={
              <Switch
                checked={isExternal}
                disabled={disabled}
                color={isExternal ? "error" : undefined}
                onChange={handleIsExternalChanged}
              />
            }
            label={
              <AudienceLabel
                isexternal={isExternal}
                disabled={disabled}
              >
                External Comment
              </AudienceLabel>
            }
          />
          {isExternal && (
            <FormHelperText component={"span"}>
              <strong>Note:</strong> This comment will be visible to everyone
              associated with this proposal, including:
              <br />
              <i>
                the proposal creator, team, and both client and vendor reviewers
              </i>
            </FormHelperText>
          )}
        </FormControl>
      </span>
    </Tooltip>
  );
}
